import * as sagas from "../../../common/sagas";
import * as config from "../../../config";
import * as utils from "../../../utils";
import { handleError, fileOverwritten } from "../../actions/error/errorActions";
import { acceptPendingSubmission } from "../../actions/meta/metaActions";
import * as types from "../../actions/persistence/persistenceActionTypes";
import * as actions from "../../actions/persistence/persistenceActions";
import { setSavedPersistentState } from "../../actions/temp/navigation/navigationActions";
import {
  selectFilename,
  selectPersistedState,
  selectSavedToken,
} from "../../selectors/persistence/persistenceSelectors";
import * as staticDataSelectors from "../../selectors/temp/staticData/staticDataSelectors";
import * as logger from "common/logger";
import {
  storeSovOverrides,
  storeSovAnalytics,
} from "components/inputs/SOV/utils/sovDataPersistence";
import { extractManualSovPricing } from "domain/manualSovPricing";
import { call, put, select, takeLatest } from "redux-saga/effects";
import { aiAgentsApi } from "services/aiAgents";
import * as attachmentActionTypes from "store/actions/input/attachments/attachmentsActionTypes";
import * as programActions from "store/actions/input/program/programActions";
import * as tempAttachActions from "store/actions/temp/attachments/attachmentsTempActions";
import * as attachmentsSelectors from "store/selectors/input/attachments/attachmentsSelectors";
import * as programSelectors from "store/selectors/input/program/programSelectors";
import {
  buildFieldUsageByFile,
  clearFieldUsage,
  getUsedFieldKeys,
} from "utils/aiSuggestionUsageTracker";
import { getSuggestionsConfig } from "utils/aiSuggestionsConfig";

// Helper function to extract file IDs from AI ingestion data
export function* markUsedFiles(submissionId) {
  try {
    const ingestionConfigData = yield select(
      staticDataSelectors.selectConfig("submission_ai_ingestion_defaults")
    );
    const config = getSuggestionsConfig(ingestionConfigData);
    const attachToSubmission = config.ATTACH_FILES_TO_SUBMISSION;

    const fieldUsageByFile = buildFieldUsageByFile();
    const usedFileIds = Object.keys(fieldUsageByFile);

    const newUsedFields = [...getUsedFieldKeys()];
    if (usedFileIds.length === 0 && newUsedFields.length === 0) {
      return;
    }

    const program = yield select(programSelectors.selectProgram);
    const existingFields = program.aiUsedFields ?? [];
    const mergedFields = [...new Set([...existingFields, ...newUsedFields])];

    yield put(
      programActions.updateProgram({
        createdFromAI: true,
        aiUsedFields: mergedFields,
      })
    );

    // Mark each used file
    for (const fileId of usedFileIds) {
      try {
        yield put(
          aiAgentsApi.endpoints.markFileAsUsed.initiate({
            fileId,
            submissionId,
            fieldUsage: fieldUsageByFile[fileId],
          })
        );
        // A file the attachments already account for stays as the user left it.
        // Creating a submission from triage can attach documents up front, and the
        // user may delete or move one before this first save.
        const accountedFor = yield select(
          attachmentsSelectors.isFileInAttachments,
          fileId
        );
        if (attachToSubmission && !accountedFor) {
          yield put({
            type: attachmentActionTypes.ATTACHMENT_UPLOADED,
            payload: { fileId },
          });
          yield put(tempAttachActions.requestAttachmentTree(fileId));
        }
      } catch (error) {
        // Log but don't fail the save if marking files fails
        logger.error(`Failed to mark file ${fileId} as used:`, error);
      }
    }
    clearFieldUsage();
  } catch (error) {
    // Log but don't fail the save
    logger.error("Failed to mark files as used:", error);
  }
}

// Large SOV fields (overrides, per-location analytics, resolved locations)
// are stored in S3 files and only the file ID is persisted in the submission
// to keep payload size small and avoid hitting the API Gateway 6MB limit.
// The manual-location entries of the per-location analytics also stay inline
// as manualSov, so materialization can read them without the file. The
// Property SOV page saves a single location, so that is one entry.
export function* cleanStateForPersistence(state) {
  const clean = { ...state };

  if (clean.SOV?.overrides && Object.keys(clean.SOV.overrides).length > 0) {
    const { overrides, ...sovRest } = clean.SOV;
    clean.SOV = {
      ...sovRest,
      overridesFileId: yield call(storeSovOverrides, overrides),
    };
  }

  // _resolvedLocations is a transient cache of the Ping-extracted locations.
  // It can be rebuilt from extractedDataFileId on load, so drop it before persisting.
  if (clean.SOV?._resolvedLocations) {
    const { _resolvedLocations, ...sovRest } = clean.SOV;
    clean.SOV = sovRest;
  }

  if (clean.analytics) {
    for (const [key, pricing] of Object.entries(clean.analytics)) {
      if (pricing?.values?.sov && Object.keys(pricing.values.sov).length > 0) {
        const { sov, sovCat, manualSov, ...restValues } = pricing.values;
        const manualSovPricing = extractManualSovPricing(sov);
        clean.analytics = {
          ...clean.analytics,
          [key]: {
            ...pricing,
            values: {
              ...restValues,
              sovAnalyticsFileId: yield call(storeSovAnalytics, {
                sov,
                sovCat,
              }),
              ...(Object.keys(manualSovPricing).length
                ? { manualSov: manualSovPricing }
                : {}),
            },
          },
        };
      }
    }
  }

  return clean;
}

export function* doSaveState20240918(allowOverwriteWarning) {
  try {
    yield put(actions.saveState.started());
    const persistedState = yield select(selectPersistedState);
    const cleanedState = yield call(cleanStateForPersistence, persistedState);
    const filename = yield select(selectFilename);
    const saved_token = yield select(selectSavedToken);

    const body = {
      state: cleanedState,
      filename,
      savedToken: saved_token,
    };
    const url = config.endpoints.persistence("update");
    const method = "put";

    const response = yield sagas.retry(
      call(utils.authenticatedFetch, url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      }),
      { maxAttempts: 3, initialDelay: 500 }
    );

    const response_json = yield call([response, response.json]);
    yield put(
      actions.saveState.succeeded({
        savedAt: response_json.savedAt,
        filename: response_json.filename,
        savedBy: response_json.savedBy,
        savedToken: response_json.savedToken,
        overwritten: response_json.overwritten,
      })
    );
    if (response_json.overwritten && allowOverwriteWarning) {
      yield put(
        fileOverwritten(
          response_json.overwritten.writerEmail,
          response_json.overwritten.writerAt
        )
      );
    }
    yield put(setSavedPersistentState(persistedState));

    // Mark AI ingestion files as used (non-blocking)
    const submissionId = response_json?.id || persistedState?.id;
    if (submissionId) {
      yield call(markUsedFiles, submissionId);
    }

    return true;
  } catch (e) {
    logger.exception(e);
    yield put(actions.saveState.failed());
    yield put(
      handleError(e, {
        title: "Failed to save.",
      })
    );
    return false;
  }
}

export function* doSaveState20240919(allowOverwriteWarning) {
  try {
    yield put(actions.saveState.started());
    const persistedState = yield select(selectPersistedState);
    const cleanedState = yield call(cleanStateForPersistence, persistedState);
    const filename = yield select(selectFilename);
    const savedToken = yield select(selectSavedToken);

    const body = {
      state: cleanedState,
      filename,
      savedToken: savedToken,
    };
    const url = config.endpoints.persistence("update");
    const method = "put";

    const response = yield sagas.retry(
      call(utils.authenticatedFetch, url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      }),
      { maxAttempts: 3, initialDelay: 500 }
    );

    const responseJson = yield call([response, response.json]);
    yield put({
      type: types.LOAD_SUBMISSION_SUCCEEDED,
      payload: {
        data: responseJson,
      },
    });
    if (responseJson.overwritten && allowOverwriteWarning) {
      yield put(
        fileOverwritten(
          responseJson.overwritten.writerEmail,
          responseJson.overwritten.writerAt
        )
      );
    }
    const newPersistedState = yield select(selectPersistedState);
    yield put(setSavedPersistentState(newPersistedState));

    // Mark AI ingestion files as used (non-blocking)
    const submissionId = responseJson?.id || persistedState?.id;
    if (submissionId) {
      yield call(markUsedFiles, submissionId);
    }

    return true;
  } catch (e) {
    logger.exception(e);
    yield put(actions.saveState.failed());
    yield put(
      handleError(e, {
        title: "Failed to save.",
      })
    );
    return false;
  }
}
export function* doSaveState(allowOverwriteWarning) {
  const featureFlags = yield select(
    staticDataSelectors.selectConfig("feature_flags")
  );
  const enableReloadOnSave =
    featureFlags?.["ui/submissionReloadOnSave"] ?? false;
  if (enableReloadOnSave) {
    return yield call(doSaveState20240919, allowOverwriteWarning);
  }
  return yield call(doSaveState20240918, allowOverwriteWarning);
}

export function* saveState() {
  return yield call(doSaveState, true);
}

function* saveStateAndRedirect(action) {
  try {
    yield put(acceptPendingSubmission());
    const success = yield call(doSaveState, false);
    if (!success) return false;
    const url = config.endpoints.exchange("accept-transfer-v2");
    yield call(utils.authenticatedFetch, url, {
      body: JSON.stringify({
        submissionId: action.payload.submissionId,
        submissionTransferId: action.payload.submissionTransferId,
      }),
      method: "post",
      headers: {
        "Content-Type": "application/json",
      },
    });
    yield put(actions.acceptedTransmissionSaved(action.payload.submissionId));
    return true;
  } catch (e) {
    logger.exception(e);
  }
}

export default function* persistenceSaga() {
  yield takeLatest(actions.saveState.requested, saveState);
  yield takeLatest(types.SAVE_ACCEPTED_TRANSMISSION, saveStateAndRedirect);
}
